import React, { useState } from 'react';
import { ChevronLeft, ChevronRight, Clock, DollarSign, Calendar, CheckCircle } from 'lucide-react';

const Services = ({ websiteData, servicesScrollRef, scroll }) => {
  const [activeService, setActiveService] = useState(null);

  return (
    <div className="relative" id="services">
      <div className="text-center mb-12">
        <h2 className="text-4xl font-bold text-purple-900 mb-4">Programs & Services</h2>
        <p className="text-gray-600 text-lg max-w-2xl mx-auto">Choose from our proven programs designed to get you results faster than traditional methods</p>
      </div>
      
      <div className="absolute right-0 top-3 hidden sm:flex space-x-2 z-10">
        <button 
          onClick={() => scroll(servicesScrollRef, 'left')} 
          className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 border border-purple-100"
          aria-label="Scroll left"
        >
          <ChevronLeft className="w-5 h-5 text-purple-700" />
        </button>
        <button 
          onClick={() => scroll(servicesScrollRef, 'right')} 
          className="p-3 rounded-full bg-white shadow-lg hover:shadow-xl transition-all duration-300 transform hover:scale-110 border border-purple-100"
          aria-label="Scroll right"
        >
          <ChevronRight className="w-5 h-5 text-purple-700" />
        </button>
      </div>
      
      <div 
        ref={servicesScrollRef}
        className="flex space-x-6 overflow-x-auto pb-6 snap-x snap-mandatory hide-scrollbar w-full max-w-full px-0"
      >
        {websiteData.services.map((service) => (
          <div 
            key={service.id} 
            className="snap-start flex-none w-full max-w-xs sm:max-w-sm md:max-w-md lg:max-w-lg bg-white border border-purple-100 rounded-2xl shadow-lg hover:shadow-2xl transition-all duration-500 transform hover:translate-y-[-8px] hover:scale-[1.02] p-8 flex flex-col min-h-[600px] group relative overflow-hidden"
            onMouseEnter={() => setActiveService(service.id)}
            onMouseLeave={() => setActiveService(null)}
          >
            {/* Animated background gradient */}
            <div className="absolute inset-0 bg-gradient-to-br from-purple-50 via-pink-25 to-purple-50 opacity-0 group-hover:opacity-100 transition-opacity duration-500 rounded-2xl"></div>
            
            <div className="relative z-10 flex flex-col h-full">
              {/* Icon and title */}
              <div className="flex items-center mb-6">
                <div className="w-16 h-16 rounded-2xl bg-gradient-to-br from-purple-500 to-purple-700 flex items-center justify-center text-white mr-4 transform group-hover:scale-110 group-hover:rotate-6 transition-all duration-300">
                  {service.icon}
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-purple-900 group-hover:text-purple-700 transition-colors duration-300">{service.title}</h3>
                  <div className="flex items-center space-x-4 mt-2 text-sm text-gray-500">
                    <div className="flex items-center">
                      <Clock className="w-4 h-4 mr-1 text-purple-500" />
                      {service.duration}
                    </div>
                    <div className="flex items-center font-bold text-purple-600">
                      <DollarSign className="w-4 h-4 mr-1" />
                      {service.price}
                    </div>
                  </div>
                </div>
              </div>
              
              {/* Description */}
              <p className="text-gray-600 mb-6 flex-grow leading-relaxed">{service.description}</p>
              
              {/* Features list */}
              <div className="mb-6">
                <h4 className="font-semibold text-purple-900 mb-3">What's Included:</h4>
                <ul className="space-y-2">
                  {service.features.map((feature, idx) => (
                    <li key={idx} className="flex items-center text-sm text-gray-600">
                      <CheckCircle className="w-4 h-4 mr-2 text-green-500 flex-shrink-0" />
                      {feature}
                    </li>
                  ))}
                </ul>
              </div>
              
              {/* CTA Button */}
              <a 
                href={service.link}
                target="_blank"
                rel="noopener noreferrer"
                className="w-full mt-auto px-6 py-4 bg-gradient-to-r from-purple-600 to-purple-800 text-white font-bold rounded-xl inline-flex items-center justify-center hover:shadow-xl transition-all duration-300 transform hover:scale-[1.02] group-hover:from-purple-700 group-hover:to-purple-900"
              >
                <Calendar className="w-5 h-5 mr-2" />
                Start Today
                <ChevronRight className="w-4 h-4 ml-2 transform group-hover:translate-x-1 transition-transform duration-300" />
              </a>
            </div>
          </div>
        ))}
      </div>

      {/* Custom CSS */}
      <style>{`
        .hide-scrollbar {
          scrollbar-width: none;
          -ms-overflow-style: none;
        }
        
        .hide-scrollbar::-webkit-scrollbar {
          display: none;
        }

        .bg-purple-25 {
          background-color: rgb(250, 248, 255);
        }
      `}</style>
    </div>
  );
};

export default Services; 